import { SectionName } from 'utils/types'
import {
  BuildingExerciseData,
  ComprehensionExerciseData,
  ConversationExerciseData,
  ExerciseType,
  ReadSentenceExerciseData,
  WordPracticeExerciseData,
  WordRaceExerciseData
} from '../Exercise/Exercise.types'
import { AchievementName, LogoutAction } from '../User/User.types'

export enum LessonElementType {
  EXERCISE = 'exercise',
  VIDEO = 'video',
  BREAKPOINT = 'breakpoint',
  SPEAKING_CHALLENGE = 'speaking-challenge'
}

export enum LessonComponentAnimation {
  READY_FOR_NEXT_ACTIVITY = 'ready for next activity',
  READ_OUT_LOUD = 'read out loud',
  READY_FOR_SPEAKING_CHALLENGE = 'ready for speaking challenge',
  FINISHED_STUDY_SET = 'finished study set',
  TUTOR_RESPONDS_SOON = 'tutor responds soon'
}

export type Lesson = {
  _id: string
  title: string
  imageUrl: string
  points: number
  hideTitle?: boolean
  routine: Routine
  sections: Sections
}

export type LessonState = {
  lessonsTemplates: Lesson[]
  activeLessons: Lesson[]
  lessonInstance: Routine | null
}

// ----- ACTIONS -----

export enum LessonActionTypes {
  GET_LESSONS_TEMPLATES = 'Lesson/get_lessons_templates',
  GET_ACTIVE_LESSONS = 'Lesson/get_active_lessons',
  CREATE_LESSON_INSTANCE = 'Lesson/create_lesson_instance',
  RESET_LESSON_INSTANCE = 'Lesson/reset_lesson_instance',
  SET_LESSON_INSTANCE = 'Lessson/set_lesson_instance',
  CHANGE_LESSON_INSTANCE_PROGRESS = 'Lesson/change_lesson_instance_progress',
  SEND_SPEAKING_CHALLANGE_VIDEO = 'Lesson/send_speaking_challenge_video'
}

export type GetLessonsTemplatesAction = {
  type: LessonActionTypes.GET_LESSONS_TEMPLATES
  lessonsTemplates: Lesson[]
}

export type SetLessonInstanceAction = {
  type: LessonActionTypes.SET_LESSON_INSTANCE
  routine: Routine
}

export type GetActiveLessonsAction = {
  type: LessonActionTypes.GET_ACTIVE_LESSONS
  activeLessons: Lesson[]
}

export type ResetLessonInstanceAction = {
  type: LessonActionTypes.RESET_LESSON_INSTANCE
}

export type ChangeLessonInstanceProgressAction = {
  type: LessonActionTypes.CHANGE_LESSON_INSTANCE_PROGRESS
  currentComponentIndex: number
}

export type LessonActions =
  | LogoutAction
  | GetActiveLessonsAction
  | GetLessonsTemplatesAction
  | SetLessonInstanceAction
  | ResetLessonInstanceAction
  | ChangeLessonInstanceProgressAction

// TUTOR TYPINGS:

export enum TutorMessageName {
  NEW_STUDY_SET = 'new-study-set',
  WILL_WATCH_VIDEO = 'will-watch-video',
  // TODO: This is specifically for having watched speaking challenge and having sent feedback!
  NEW_VIDEO_MESSAGE = 'new-video-message',
  KUDOS = 'kudos',
  /** Generic message for saying that that a tutor has sent a video */
  SENT_NEW_VIDEO_MESSAGE = 'sent-new-video-message',
  PT_ONBOARDING = 'private-tutoring-onboarding'
}

type UnparameterizedTutorMessage = {
  message:
    | TutorMessageName.NEW_STUDY_SET
    | TutorMessageName.WILL_WATCH_VIDEO
    | TutorMessageName.KUDOS
    | TutorMessageName.PT_ONBOARDING
}

export type TutorMessage =
  | UnparameterizedTutorMessage
  | { message: TutorMessageName.NEW_VIDEO_MESSAGE; studySetName: string }

export type TutorProfile = {
  firstName: string
  lastName: string
  avatarUrl?: string
}

// ROUTINE:

export type Routine = {
  id: string
  title: string
  resumeAtActivityIndex?: number
  activities: Activity[]
}

export enum CreationContext {
  Library = 'library',
  LearningJourney = 'learning-journey'
}

export enum EffectType {
  SetFallbackTutoringMessage = 'set-fallback-tutoring-message'
}

export type Effect = {
  type: EffectType.SetFallbackTutoringMessage
  tutorProfile: TutorProfile
  message: TutorMessage
}

export type Activity = ActivityBase &
  (
    | AwardActivity
    | ExerciseActivity
    | Exercise2Activity
    | RecordVideoActivity
    | VideoActivity
    | WordPracticeIntroActivity
    | WordPracticeActivity
    | WordPracticeSummary
  )

export enum ActivityType {
  Award = 'award',
  Exercise = 'exercise',
  RecordVideoActivity = 'record-video',
  Video = 'video',
  WordPracticeIntro = 'word-practice-intro',
  WordPractice = 'word-practice',
  Exercise2 = 'exercise2',
  WordPracticeSummary = 'word-practice-summary'
}

export enum SentenceType {
  Listening = 'Listening',
  Reading = 'Reading'
}

export type Sections = {
  sectionNames: SectionName[]
  finishedSectionCount: number
}

export type ActivityBase = {
  animation?: LessonComponentAnimation
  progress?: { current: number; total: number }
  activityReference?: string
  postEffects?: Effect[]
}

export type AwardActivity = {
  type: ActivityType.Award
  points?: number
  bumpSection?: Sections
  achievementNames?: AchievementName[]
}

export type ExerciseActivity = {
  type: ActivityType.Exercise
  exerciseId: string
  sentenceType?: SentenceType
  exercise: ExerciseBlob
}

export type Exercise2Activity = {
  type: ActivityType.Exercise2
  activityReference: string
  exercise: Exercise2Blob
}

export type RecordVideoActivity = {
  type: ActivityType.RecordVideoActivity
  promptText: string
}

export type VideoActivity = {
  type: ActivityType.Video
  videoId: string
  videoUrl: string
  sectionProgress?: Sections
  goal?: { introWithGoalText: string } | { feedbackWithGoalText: string }
  endingExercise?: VideoEndingExercise
  skippable?: boolean
}

export type WordPracticeIntroEntry = {
  word: string
  previousScore: number
}

export type WordPracticeSummaryEntry = {
  word: string
  previousScore: number
  newScore: number
}

export type WordPracticeIntroActivity = {
  type: ActivityType.WordPracticeIntro
  title: {
    type: string
    soundIpa: string
  }
  entries: WordPracticeIntroEntry[]
}

export type Syllable = { phonemesIpa: string[] }

export type WordPracticeEmphasis = { phonemeIpa: string }

export type WordPracticeActivity = {
  type: ActivityType.WordPractice
  word: string
  activityReference: string
  syllables: Syllable[]
  emphasis: WordPracticeEmphasis
}

export enum WordPracticeSummaryTitleType {
  ImprovedSound = 'improved-sound',
  WellDone = 'well-done'
}

export type WordPracticeSummaryData = {
  [key: string]: {
    wordPracticeSummaryEntries: WordPracticeSummaryEntry[]
  }
}

export type DynamicDataExpression = {
  $dynamicRemoteValue: { batch: string; valueReference: string }
}

export type WordPracticeSummary = {
  type: ActivityType.WordPracticeSummary
  title:
    | { type: WordPracticeSummaryTitleType.WellDone }
    | { type: WordPracticeSummaryTitleType.ImprovedSound; soundIpa: string }
  entries: DynamicDataExpression
  restartAtActivityIndex?: number
}
export type VideoEndingExercise = { exercise: Exercise2Blob }

export type ExerciseBlob =
  | { _id: string; type: ExerciseType.ReadWordRace; data: WordRaceExerciseData }
  | { _id: string; type: ExerciseType.ReadSentence; data: ReadSentenceExerciseData }
  | { _id: string; type: ExerciseType.SentenceBuilder; data: BuildingExerciseData }
  | { _id: string; type: ExerciseType.WordBuilder; data: BuildingExerciseData }
  | { _id: string; type: ExerciseType.MultiChoiseQuestion; data: ComprehensionExerciseData }
  | { _id: string; type: ExerciseType.WordPractice; data: WordPracticeExerciseData }
  | { _id: string; type: ExerciseType.Conversation; data: ConversationExerciseData }

export type Exercise2Blob =
  | {
      type: ExerciseType.ReadSentence
      data: ReadSentenceExerciseData
      imageUrl?: string
    }
  | {
      type: ExerciseType.MultiChoiseQuestion
      data: ComprehensionExerciseData
    }
  | {
      type: ExerciseType.SentenceBuilder
      data: BuildingExerciseData
    }
  | {
      type: ExerciseType.WordBuilder
      data: BuildingExerciseData
    }
